import { useEffect, useState, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';

// Custom hook to sync missional scale level and its Firestore metadata
export default function useMissionalScaleLevel(discipleId, userAppID) {
  const [currentLevel, setCurrentLevel] = useState(null);
  const [descriptor, setDescriptor] = useState('');
  const [phrase, setPhrase] = useState('');
  const [loading, setLoading] = useState(true);
  const [levelsMap, setLevelsMap] = useState({});

  // Fetch all missional scale levels (once)
  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const levelsCol = collection(db, 'missionalScaleLevels');
        const snapshot = await getDocs(levelsCol);
        const map = {};
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          map[data.level] = data;
        });
        setLevelsMap(map);
      } catch (err) {
        // Silently handle permission errors - don't show them to user
        const errorCode = err?.code;
        if (errorCode === 'permission-denied' || errorCode === 'firestore/permission-denied') {
          console.warn('Permission denied accessing missionalScaleLevels (non-fatal)');
        } else {
          console.error('Error fetching missional scale levels:', err);
        }
        // Set empty map on error
        setLevelsMap({});
      }
    };
    fetchLevels().catch((err) => {
      // Extra safety: catch any unhandled rejections
      const errorCode = err?.code;
      if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
        console.error('Unhandled error in fetchLevels:', err);
      }
      setLevelsMap({});
    });
  }, []);

  // Fetch current level for this disciple/user
  useEffect(() => {
    if (!discipleId || !userAppID) return;
    setLoading(true);
    const fetchCurrent = async () => {
      try {
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
        const mentorDoc = await getDoc(mentorDetailsRef);
        let level = null;
        if (mentorDoc.exists()) {
          level = mentorDoc.data().missionalScale;
        }
        setCurrentLevel(level);
      } catch (err) {
        // Silently handle permission errors - don't show them to user
        const errorCode = err?.code;
        if (errorCode === 'permission-denied' || errorCode === 'firestore/permission-denied') {
          console.warn('Permission denied accessing mentorDetails (non-fatal)');
        } else {
          console.error('Error fetching missional scale:', err);
        }
        setCurrentLevel(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCurrent().catch((err) => {
      // Extra safety: catch any unhandled rejections
      const errorCode = err?.code;
      if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
        console.error('Unhandled error in fetchCurrent:', err);
      }
      setCurrentLevel(null);
      setLoading(false);
    });
  }, [discipleId, userAppID]);

  // Update descriptor/phrase when currentLevel or levelsMap changes
  useEffect(() => {
    if (currentLevel !== null && levelsMap && levelsMap[currentLevel] !== undefined) {
      setDescriptor(levelsMap[currentLevel].descriptor || '');
      setPhrase(levelsMap[currentLevel].phrase || '');
    } else {
      setDescriptor('');
      setPhrase('');
    }
  }, [currentLevel, levelsMap]);

  // Setter to update level in Firestore
  const setLevel = useCallback(async (newLevel) => {
    if (!discipleId || !userAppID) return;
    setCurrentLevel(newLevel);
    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      await setDoc(mentorDetailsRef, {
        missionalScale: newLevel,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (err) {
      // Silently handle permission errors - don't show them to user
      const errorCode = err?.code;
      if (errorCode === 'permission-denied' || errorCode === 'firestore/permission-denied') {
        console.warn('Permission denied updating missional scale (non-fatal)');
      } else {
        console.error('Error updating missional scale:', err);
      }
      // Revert to previous level on error
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      try {
        const mentorDoc = await getDoc(mentorDetailsRef);
        if (mentorDoc.exists()) {
          setCurrentLevel(mentorDoc.data().missionalScale);
        }
      } catch (e) {
        // Ignore revert errors
      }
    }
  }, [discipleId, userAppID]);

  return { currentLevel, descriptor, phrase, setLevel, loading };
} 